// @core/stores/landing/LandingStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { landingApi } from '@core/api';
import type {
  ArchiveLandingPageRequest,
  CreateLandingPageRequest,
  CreateLandingTemplateRequest,
  DuplicateLandingPageRequest,
  InstallLandingTemplateRequest,
  LandingAnalyticsResponse,
  LandingPage,
  LandingPageResponse,
  LandingTemplateResponse,
  ListLandingTemplatesResponse,
  PublishLandingPageRequest,
  UpdateLandingPageRequest,
  UpdateSectionsRequest,
} from '@core/types/landing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class LandingStore {
  // List state
  items: LandingPage[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: LandingPage | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, LandingPage>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): LandingPage | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: LandingPage | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Archive landing page
   */
  async ArchiveLandingPage(page_id: string, data: ArchiveLandingPageRequest): Promise<LandingPageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await landingApi.ArchiveLandingPage(page_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new landing page
   */
  async CreateLandingPage(data: CreateLandingPageRequest): Promise<LandingPage> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await landingApi.CreateLandingPage(data);
      const item = response.page;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create landing template
   */
  async CreateLandingTemplate(data: CreateLandingTemplateRequest): Promise<LandingTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await landingApi.CreateLandingTemplate(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete landing page
   */
  async DeleteLandingPage(page_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await landingApi.DeleteLandingPage(page_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== page_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === page_id) {
          this.currentItem = null;
        }

        this.removeFromCache(page_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Duplicate landing page
   */
  async DuplicateLandingPage(page_id: string, data: DuplicateLandingPageRequest): Promise<LandingPageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await landingApi.DuplicateLandingPage(page_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get landing page analytics
   */
  async GetLandingAnalytics(page_id: string): Promise<LandingAnalyticsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await landingApi.GetLandingAnalytics(page_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get landing page by ID
   */
  async GetLandingPage(page_id: string): Promise<LandingPage> {
    // Check cache first
    const cached = this.cache.get(page_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await landingApi.GetLandingPage(page_id);
      const item = response.page;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get landing page by slug
   */
  async GetLandingPageBySlug(slug: string): Promise<LandingPage> {
    // Check cache first
    const cached = this.cache.get(slug);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await landingApi.GetLandingPageBySlug(slug);
      const item = response.page;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get landing template
   */
  async GetLandingTemplate(template_id: string): Promise<LandingTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await landingApi.GetLandingTemplate(template_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Install landing template
   */
  async InstallLandingTemplate(template_id: string, data: InstallLandingTemplateRequest): Promise<LandingPageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await landingApi.InstallLandingTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List landing pages
   */
  async ListLandingPages(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await landingApi.ListLandingPages(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List landing templates
   */
  async ListLandingTemplates(params?: PaginatedRequest): Promise<ListLandingTemplatesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await landingApi.ListLandingTemplates(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Publish landing page
   */
  async PublishLandingPage(page_id: string, data: PublishLandingPageRequest): Promise<LandingPageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await landingApi.PublishLandingPage(page_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update landing page
   */
  async UpdateLandingPage(page_id: string, data: UpdateLandingPageRequest): Promise<LandingPage> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await landingApi.UpdateLandingPage(page_id, data);
      const item = response.page;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update page sections
   */
  async UpdateSections(page_id: string, data: UpdateSectionsRequest): Promise<LandingPage> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await landingApi.UpdateSections(page_id, data);
      const item = response.page;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const landingStore = new LandingStore();
